import { useMemo } from 'react';
import { useSelectedStation } from '@features/stations/hooks/useSelectedStation';
import { REQUIREMENTS, SETUP_STEP_ORDER, type RequirementMeta } from './requirements';
import { useStationReadiness } from './useStationReadiness';

export interface SetupStepState {
  key: string;
  emoji: string;
  to: RequirementMeta['to'];
  done: boolean;
}

export interface SetupProgress {
  /** True until we know the real state (no station, loading, or errored). */
  isPending: boolean;
  steps: SetupStepState[];
  requiredDone: number;
  requiredTotal: number;
  allDone: boolean;
  /** First not-done step, to highlight the "next" action. */
  nextStepKey: string | null;
}

/**
 * Flattens the per-logbook readiness into an ordered setup checklist for the
 * floating guide. A step is "done" when its requirement key no longer appears in
 * any logbook's `missing` list. Fail-open: while loading, on error, or with no
 * station selected we report `isPending` so the guide simply hides (no flash,
 * never a false "everything pending").
 */
export function useSetupProgress(): SetupProgress {
  const { selectedStationId } = useSelectedStation();
  const readiness = useStationReadiness(selectedStationId);

  const isPending = !selectedStationId || readiness.isPending || readiness.isError;

  return useMemo(() => {
    const missing = new Set(readiness.data?.modules.flatMap((module) => module.missing) ?? []);

    const steps: SetupStepState[] = SETUP_STEP_ORDER.map((key) => {
      const meta = REQUIREMENTS[key];
      return meta ? { key, emoji: meta.emoji, to: meta.to, done: !missing.has(key) } : null;
    }).filter((step): step is SetupStepState => step !== null);

    const requiredDone = steps.filter((step) => step.done).length;

    return {
      isPending,
      steps,
      requiredDone,
      requiredTotal: steps.length,
      allDone: steps.length > 0 && requiredDone === steps.length,
      nextStepKey: steps.find((step) => !step.done)?.key ?? null
    };
  }, [isPending, readiness.data]);
}
